import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { AIGatewayError } from "./ai-gateway";

const GATEWAY_URL = "https://ai.gateway.lovable.dev/v1/chat/completions";

/**
 * Transcribe an audio recording stored in the `interview-recordings` bucket using
 * Gemini's multimodal Chat Completions endpoint via the Lovable AI gateway.
 * No extra API key required — uses the existing LOVABLE_API_KEY.
 */
export const transcribeInterview = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(
    (input: { audioPath: string }) => {
      if (!input?.audioPath || typeof input.audioPath !== "string") {
        throw new Error("audioPath is required");
      }
      return input;
    },
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const apiKey = process.env.LOVABLE_API_KEY;
    if (!apiKey) throw new AIGatewayError(500, "LOVABLE_API_KEY not configured");

    // Defence in depth: ensure path is inside the user's folder
    if (!data.audioPath.startsWith(`${userId}/`)) {
      throw new Response("Forbidden", { status: 403 });
    }

    // Download the file (RLS-scoped through authed client)
    const { data: blob, error: dlErr } = await supabase.storage
      .from("interview-recordings")
      .download(data.audioPath);
    if (dlErr || !blob) {
      throw new Response(`Audio not found: ${dlErr?.message ?? "unknown"}`, { status: 404 });
    }

    const buf = await blob.arrayBuffer();
    // Use Buffer for safe base64 of large payloads (avoids stack overflow from spread)
    const base64 = Buffer.from(buf).toString("base64");

    // Gemini multimodal accepts audio as input_audio in the OpenAI-compatible payload.
    // Format hint: webm/opus from MediaRecorder.
    const res = await fetch(GATEWAY_URL, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${apiKey}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        model: "google/gemini-2.5-flash",
        messages: [
          {
            role: "system",
            content:
              "You are an exact transcription engine. Output ONLY the verbatim spoken words, including filler words like 'um' and 'uh'. No commentary, no headings, no timestamps.",
          },
          {
            role: "user",
            content: [
              { type: "text", text: "Transcribe this audio recording verbatim." },
              {
                type: "input_audio",
                input_audio: { data: base64, format: "webm" },
              },
            ],
          },
        ],
      }),
    });

    if (!res.ok) {
      const text = await res.text().catch(() => "");
      if (res.status === 429)
        throw new AIGatewayError(429, "Rate limit exceeded. Try again shortly.");
      if (res.status === 402)
        throw new AIGatewayError(402, "AI credits exhausted. Add credits in Lovable workspace.");
      throw new AIGatewayError(res.status, `Transcription failed: ${text.slice(0, 300)}`);
    }

    const json = (await res.json()) as {
      choices?: Array<{ message?: { content?: string } }>;
    };
    const transcript = (json.choices?.[0]?.message?.content ?? "").trim();
    return { transcript };
  });
